/**
 * Dentis Discount Engine
 *
 * Resolves an active storefront discount code via Shopify's own cart machinery
 * and exposes it as `window.DentisActiveDiscount` plus a `dentis:discount-resolved`
 * DOM event so price snippets and the promo-status block can react.
 *
 * Source of truth: Shopify (cart.discount_applications + line_item.discount_allocations).
 * We never re-implement discount math; we mirror what Shopify computes.
 *
 * Failure mode: silent. On any error, `window.DentisActiveDiscount` is set to
 * a benign null state, the resolved event still fires (with `active: false`),
 * and price rendering falls back to the normal Liquid-rendered markup.
 */
(function () {
  'use strict';

  if (!window.DentisDebug) {
    var debugFlag = false;
    try {
      var qs = new URLSearchParams(window.location.search || '');
      if (qs.get('dentis_debug') === '1' || qs.get('dentis_debug') === 'true') debugFlag = true;
    } catch (e) { /* no-op */ }
    try {
      if (window.localStorage && window.localStorage.getItem('dentis:debug') === '1') debugFlag = true;
    } catch (e) { /* no-op */ }
    var noop = function () {};
    var T0 = (function () { try { return performance.now(); } catch (e) { return Date.now(); } })();
    var SEQ = 0;
    function nowDelta() {
      try { return Math.round(performance.now() - T0); } catch (e) { return Date.now() - T0; }
    }
    function pad(n, len) { var s = String(n); while (s.length < len) s = ' ' + s; return s; }
    function pad0(n, len) { var s = String(n); while (s.length < len) s = '0' + s; return s; }
    // Structured trace: every entry gets a monotonic seq #, a relative-to-init
    // timestamp, and a phase tag. Output looks like:
    //
    //   [Dentis #001    12ms ENG-INIT] engine boot {debug:true}
    //   [Dentis #002    45ms RESOLVE  ] cookieCode=18MAI-... picked=18MAI-...
    //   [Dentis #003    51ms P1-CART  ] discount_codes=[{...}] item_count=0
    //
    // Use this to correlate engine events with paint events (which carry the
    // same seq numbers via window.DentisDebug.seq()).
    function trace(level, phase, msg, payload) {
      SEQ++;
      var prefix = '[Dentis #' + pad0(SEQ, 3) + ' ' + pad(nowDelta(), 5) + 'ms ' + pad(phase, 9) + ']';
      var color = level === 'error' ? '#cc0000' : (level === 'warn' ? '#f5a524' : '#0e01ab');
      var args = ['%c' + prefix, 'color:' + color + ';font-weight:bold;', msg];
      if (payload !== undefined) args.push(payload);
      try {
        if (level === 'error') { console.error.apply(console, args); }
        else if (level === 'warn') { console.warn.apply(console, args); }
        else { console.log.apply(console, args); }
      } catch (e) { /* swallow */ }
    }
    window.DentisDebug = {
      enabled: debugFlag,
      log: debugFlag ? (console.log.bind(console, '%c[DentisDiscount]', 'color:#0e01ab;font-weight:bold;')) : noop,
      group: debugFlag ? (console.groupCollapsed ? console.groupCollapsed.bind(console) : console.log.bind(console)) : noop,
      groupEnd: debugFlag ? (console.groupEnd ? console.groupEnd.bind(console) : noop) : noop,
      warn: debugFlag ? (console.warn.bind(console, '%c[DentisDiscount]', 'color:#f5a524;font-weight:bold;')) : noop,
      error: debugFlag ? (console.error.bind(console, '%c[DentisDiscount]', 'color:#cc0000;font-weight:bold;')) : noop,
      trace: debugFlag ? function (phase, msg, payload) { trace('log', phase, msg, payload); } : noop,
      traceWarn: debugFlag ? function (phase, msg, payload) { trace('warn', phase, msg, payload); } : noop,
      traceError: function (phase, msg, payload) { trace('error', phase, msg, payload); },
      seq: function () { return SEQ; }
    };
  }
  var DBG = window.DentisDebug;
  // Local shortcut so the rest of the file can write DBG.t('PHASE', 'msg', {...})
  // instead of DBG.trace('PHASE', 'msg', {...}). Same for warn/error variants.
  DBG.t = DBG.trace;
  DBG.tw = DBG.traceWarn;
  DBG.te = DBG.traceError;

  if (window.DentisDiscountEngine) {
    DBG.log('engine already initialized, skipping re-init');
    return;
  }

  var STORAGE_KEY = 'dentis:active-discount';
  var STORAGE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  var COOKIE_NAME = 'discount_code';
  var URL_PARAMS = ['discount', 'discount_code'];
  var EVENT_NAME = 'dentis:discount-resolved';

  // ────────────────────────────────────────────────────────────────────────
  // Combinability module
  //
  // Mirrors Shopify Admin's "Combines with" toggles. Every discount carries
  // a `kind` ('product' | 'order') and a `combines: {order, product}` shape.
  //
  // The matrix (from real-world Shopify behavior, NOT a textbook reading of
  // the docs):
  //
  //   1. Within a single kind (product-vs-product or order-vs-order):
  //        - All discounts with combines[kind]=true ("permissive") apply
  //          unconditionally.
  //        - Among discounts with combines[kind]=false ("exclusive"), only
  //          the one with the largest savings applies; the rest are dropped.
  //
  //   2. Cross-kind (product-vs-order):
  //        - The pair coexists iff BOTH sides allow the other side's kind.
  //          I.e. product.combines.order AND order.combines.product. If
  //          either is false, the order-side discount is dropped (since
  //          product-side is typically the "primary" customer-paying line).
  //
  // The default for any missing combines value is true (permissive), so
  // older JSON entries without combines blocks behave like permissive
  // discounts (no behavior change for them).
  // ────────────────────────────────────────────────────────────────────────
  var Combinability = (function () {
    function normalize(c) {
      var src = (c && c.combines) || {};
      return {
        order: src.order !== false,    // default true
        product: src.product !== false // default true
      };
    }
    function kindOf(d) {
      // Storewide single is order-kind by convention (hardcoded). Everything
      // else in the campaigns JSON is product-kind.
      return (d && d.kind) === 'order' ? 'order' : 'product';
    }
    return { normalize: normalize, kindOf: kindOf };
  })();

  // Influencer auto-recognition. Codes following the convention
  // `<LETTERS>20` (case-insensitive) are treated as order-level 20%-off
  // discounts that combine with everything. The pattern is intentionally
  // tight: contiguous A–Z only, ending with the literal digits `20`. This
  // rejects campaign codes that happen to contain "20" (e.g.
  // `18MAI-1200KR-GAVEKORT-DUOPAKKEN`, `LINE-20`, `LINE20A`).
  var INFLUENCER_PATTERN = /^[A-Z]+20$/i;
  var INFLUENCER_DEFAULT_PERCENTAGE = 20;
  function isInfluencerCode(code) {
    if (!code) return false;
    return INFLUENCER_PATTERN.test(String(code).trim());
  }

  function readStaticPromoConfig() {
    try {
      var cfg = window.DentisStaticPromoConfig;
      if (!cfg) return null;
      var out = { current: cfg.current || {} };

      if (cfg.code) {
        var value = Number(cfg.value);
        if (isFinite(value) && value > 0) {
          var type = (cfg.type === 'fixed' || cfg.type === 'fixed_amount') ? 'fixed' : 'percentage';
          out.single = { code: String(cfg.code).trim(), type: type, value: value };
        }
      }

      if (Array.isArray(cfg.campaigns) && cfg.campaigns.length > 0) {
        // Filter out campaigns with active:false so they're invisible to every
        // downstream code path (matchCampaign / findCampaignByCode /
        // collectCoreCampaigns / cookie-truth reconciliation). Missing or
        // active:true keeps the entry active — backward-compatible.
        var activeCampaigns = cfg.campaigns.filter(function (c) {
          return c && c.active !== false;
        });
        if (activeCampaigns.length > 0) {
          out.campaigns = activeCampaigns;
        }
      }

      if (!out.single && !out.campaigns) return null;
      return out;
    } catch (e) { return null; }
  }

  function matchCampaign(staticCfg, code) {
    if (!staticCfg || !Array.isArray(staticCfg.campaigns)) return null;
    var codeLc = (code || '').toLowerCase().trim();
    if (!codeLc) return null;
    var current = staticCfg.current || {};
    var currentTemplate = String(current.template_suffix || '').toLowerCase();
    var currentHandle = String(current.product_handle || '').toLowerCase();
    var currentVariantIds = Array.isArray(current.product_variant_ids) ? current.product_variant_ids : [];
    var currentVariantIdSet = {};
    for (var cvi = 0; cvi < currentVariantIds.length; cvi++) {
      currentVariantIdSet[String(currentVariantIds[cvi])] = true;
    }

    for (var i = 0; i < staticCfg.campaigns.length; i++) {
      var c = staticCfg.campaigns[i];
      if (!c || !c.code) continue;
      if (String(c.code).toLowerCase().trim() !== codeLc) continue;

      var templates = Array.isArray(c.templates) ? c.templates : [];
      var handles = Array.isArray(c.product_handles) ? c.product_handles : [];
      var variantIds = Array.isArray(c.variant_ids) ? c.variant_ids : [];

      // All three scopes are AND. Empty array = no scope restriction on that axis.
      var templateOk = true;
      var handleOk = true;
      var variantOk = true;
      if (templates.length > 0) {
        templateOk = false;
        for (var ti = 0; ti < templates.length; ti++) {
          if (String(templates[ti]).toLowerCase() === currentTemplate) { templateOk = true; break; }
        }
      }
      if (handles.length > 0) {
        handleOk = false;
        for (var hi = 0; hi < handles.length; hi++) {
          if (String(handles[hi]).toLowerCase() === currentHandle) { handleOk = true; break; }
        }
      }
      if (variantIds.length > 0) {
        // Campaign matches if the current PDP has at least one variant in the
        // campaign's variant_ids list. Per-variant discount restriction (so
        // unlisted variants of the same product stay full price) is handled
        // downstream in buildResolvedFromCampaign + getDiscountForVariant.
        variantOk = false;
        for (var vi = 0; vi < variantIds.length; vi++) {
          if (currentVariantIdSet[String(variantIds[vi])]) { variantOk = true; break; }
        }
      }
      if (templateOk && handleOk && variantOk) return c;
    }
    return null;
  }

  function findCampaignByCode(staticCfg, code) {
    if (!staticCfg || !Array.isArray(staticCfg.campaigns)) return null;
    var codeLc = (code || '').toLowerCase().trim();
    if (!codeLc) return null;
    for (var i = 0; i < staticCfg.campaigns.length; i++) {
      var c = staticCfg.campaigns[i];
      if (!c || !c.code) continue;
      if (String(c.code).toLowerCase().trim() === codeLc) return c;
    }
    return null;
  }

  function campaignMatchesCurrent(campaign, current) {
    if (!campaign) return false;
    var currentTemplate = String(current.template_suffix || '').toLowerCase();
    var currentHandle = String(current.product_handle || '').toLowerCase();
    var currentVariantIds = Array.isArray(current.product_variant_ids) ? current.product_variant_ids : [];
    var currentVariantIdSet = {};
    for (var cvi = 0; cvi < currentVariantIds.length; cvi++) {
      currentVariantIdSet[String(currentVariantIds[cvi])] = true;
    }
    var templates = Array.isArray(campaign.templates) ? campaign.templates : [];
    var handles = Array.isArray(campaign.product_handles) ? campaign.product_handles : [];
    var variantIds = Array.isArray(campaign.variant_ids) ? campaign.variant_ids : [];
    if (templates.length > 0) {
      var tok = false;
      for (var ti = 0; ti < templates.length; ti++) {
        if (String(templates[ti]).toLowerCase() === currentTemplate) { tok = true; break; }
      }
      if (!tok) return false;
    }
    if (handles.length > 0) {
      var hok = false;
      for (var hi = 0; hi < handles.length; hi++) {
        if (String(handles[hi]).toLowerCase() === currentHandle) { hok = true; break; }
      }
      if (!hok) return false;
    }
    if (variantIds.length > 0) {
      var vok = false;
      for (var vi = 0; vi < variantIds.length; vi++) {
        if (currentVariantIdSet[String(variantIds[vi])]) { vok = true; break; }
      }
      if (!vok) return false;
    }
    return true;
  }

  // Walk a primary campaign's `also_apply` list (recursively), collecting every
  // matching child campaign for the current PDP. Cycle-safe via visited set.
  // Returns array of child campaigns, in declaration order, that match the
  // current PDP scope. Children whose scope doesn't intersect with the current
  // PDP are skipped from the display layer but still applied at the cart layer
  // (see stackChildDiscountsOnCart).
  function collectChildCampaigns(staticCfg, primary) {
    var out = [];
    if (!primary || !Array.isArray(primary.also_apply) || primary.also_apply.length === 0) return out;
    var visited = {};
    visited[String(primary.code || '').toLowerCase().trim()] = true;
    var current = staticCfg.current || {};

    function walk(codeList) {
      for (var i = 0; i < codeList.length; i++) {
        var code = String(codeList[i] || '').trim();
        var key = code.toLowerCase();
        if (!code || visited[key]) continue;
        visited[key] = true;
        var child = findCampaignByCode(staticCfg, code);
        if (!child) continue;
        if (campaignMatchesCurrent(child, current)) out.push(child);
        if (Array.isArray(child.also_apply) && child.also_apply.length > 0) walk(child.also_apply);
      }
    }
    walk(primary.also_apply);
    return out;
  }

  // Returns the list of child codes (regardless of PDP scope) that should be
  // stacked on the cart by /discount/<code>?redirect=/cart.js. Used for the
  // checkout-side effect; display layer uses the scoped list above.
  function collectChildCodesForCart(staticCfg, primary) {
    var out = [];
    if (!primary || !Array.isArray(primary.also_apply) || primary.also_apply.length === 0) return out;
    var visited = {};
    visited[String(primary.code || '').toLowerCase().trim()] = true;

    function walk(codeList) {
      for (var i = 0; i < codeList.length; i++) {
        var code = String(codeList[i] || '').trim();
        var key = code.toLowerCase();
        if (!code || visited[key]) continue;
        visited[key] = true;
        out.push(code);
        var child = findCampaignByCode(staticCfg, code);
        if (child && Array.isArray(child.also_apply) && child.also_apply.length > 0) walk(child.also_apply);
      }
    }
    walk(primary.also_apply);
    return out;
  }

  function buildResolvedFromStaticConfig(code, cfg) {
    return {
      active: true,
      code: code,
      type: cfg.type,
      value: cfg.value,
      value_type: cfg.type === 'percentage' ? 'percentage' : 'fixed_amount',
      eligible: {},
      target_selection: 'all',
      target_type: 'line_item',
      raw_app: null,
      cart_token: null,
      source: 'static-config',
      // Storewide single is by convention an order-level discount that
      // combines with everything. Hardcoded — not exposed in theme settings.
      kind: 'order',
      combines: { order: true, product: true },
      core: false
    };
  }

  // Walk all configured campaigns and return the ones marked `core: true`
  // whose scope (templates / handles / variant_ids) intersects the current
  // PDP. These are always-on default-paint discounts (e.g. a bundle's
  // intrinsic deal price) that exist alongside whatever campaign cookie a
  // customer arrives with.
  //
  // The engine includes cores in EVERY resolve flow (cookie-matched
  // campaign, cookie-matched storewide single, or no-cookie-at-all). Per-
  // variant composition in price-override decides whether each core
  // survives the combinability matrix vs the cookie-matched discount.
  function collectCoreCampaigns(staticCfg) {
    if (!staticCfg || !Array.isArray(staticCfg.campaigns)) return [];
    var out = [];
    var current = staticCfg.current || {};
    for (var i = 0; i < staticCfg.campaigns.length; i++) {
      var c = staticCfg.campaigns[i];
      if (!c || c.core !== true) continue;
      if (campaignMatchesCurrent(c, current)) out.push(c);
    }
    return out;
  }

  // Build a self-contained sub-resolved for a single campaign. The "code"
  // param is the cookie / chain entry-point code; for the primary it equals
  // campaign.code, for children it's the child's own code.
  function buildSingleCampaignResolved(code, campaign, current) {
    var eligible = {};
    var campaignVariantIds = Array.isArray(campaign.variant_ids) ? campaign.variant_ids : [];
    if (current && current.product_id) {
      var bucket = { savings_total: 0, variants: {} };
      if (campaignVariantIds.length > 0) {
        bucket.restrict_to_variants = true;
        for (var bvi = 0; bvi < campaignVariantIds.length; bvi++) {
          bucket.variants[String(campaignVariantIds[bvi])] = 0;
        }
      }
      eligible[String(current.product_id)] = bucket;
    }

    var pct = Number(campaign.percentage || 0);
    var amt = Number(campaign.amount || 0);
    var type, value;
    if (pct > 0) {
      type = 'percentage';
      value = pct;
    } else if (amt > 0) {
      type = 'fixed';
      value = amt;
    } else {
      type = 'percentage';
      value = 0;
    }

    return {
      active: true,
      code: code,
      type: type,
      value: value,
      value_type: type === 'percentage' ? 'percentage' : 'fixed_amount',
      eligible: eligible,
      target_selection: 'entitled',
      target_type: 'line_item',
      source: 'static-config:campaign',
      campaign_code: campaign.code || null,
      // Combinability + kind metadata used by price-override's per-variant
      // composer. Campaigns are product-kind by default, but honor an
      // explicit `kind: "order"` in the JSON (e.g. VELKOMMEN15). Order-kind
      // subs MUST be classified as such so the composer sequences them AFTER
      // product discounts — Shopify allocates order discounts on the
      // post-line-discount subtotal, so a 15% order code applies to the price
      // that remains after a fixed product deal (DUO-PAKKEPRIS), not to the
      // compare-at/base price in isolation.
      kind: Combinability.kindOf(campaign),
      combines: Combinability.normalize(campaign),
      core: campaign.core === true
    };
  }

  // Build the full resolved payload for a primary campaign and any child
  // campaigns chained via `also_apply`. The returned object exposes:
  //   - root: top-level fields mirror the PRIMARY for backward compat with
  //     anything reading `resolved.type` / `resolved.value` / `resolved.code`
  //   - resolved.discounts: array of every matching sub-resolved (primary
  //     first), so price-override can iterate per-variant and pick the
  //     first one that matches.
  // Build a resolved payload that contains ONLY cores. Used when the
  // customer has no campaign cookie (and no storewide single match) but the
  // current PDP has core discounts to paint.
  function buildCoresOnlyResolved(coreCampaigns, current) {
    if (!coreCampaigns || coreCampaigns.length === 0) {
      return { active: false, code: null, source: 'cores-only:empty' };
    }
    var subs = coreCampaigns.map(function (c) {
      return buildSingleCampaignResolved(c.code, c, current);
    });
    var first = subs[0];
    return {
      active: true,
      code: first.code,
      type: first.type,
      value: first.value,
      value_type: first.value_type,
      eligible: first.eligible,
      target_selection: first.target_selection,
      target_type: first.target_type,
      source: 'cores-only',
      discounts: subs
    };
  }

  // Build a resolved payload for an influencer-pattern cookie code. The
  // synthesized sub is order-kind 20% off, fully combinable. Cores (if any)
  // are prepended so the per-variant matrix composer in price-override
  // stacks the influencer % on top of the core deal naturally (cross-kind
  // product+order, both combines all-true).
  function buildInfluencerResolved(code, coreCampaigns, current) {
    var influencerSub = {
      active: true,
      code: code,
      type: 'percentage',
      value: INFLUENCER_DEFAULT_PERCENTAGE,
      value_type: 'percentage',
      eligible: {},
      target_selection: 'all',
      target_type: 'line_item',
      source: 'influencer-pattern',
      kind: 'order',
      combines: { order: true, product: true },
      core: false
    };
    var subs = [];
    if (Array.isArray(coreCampaigns)) {
      for (var i = 0; i < coreCampaigns.length; i++) {
        subs.push(buildSingleCampaignResolved(coreCampaigns[i].code, coreCampaigns[i], current));
      }
    }
    subs.push(influencerSub);
    return {
      active: true,
      code: code,
      type: influencerSub.type,
      value: influencerSub.value,
      value_type: influencerSub.value_type,
      eligible: {},
      target_selection: 'all',
      target_type: 'line_item',
      source: subs.length > 1 ? 'influencer+cores' : 'influencer',
      discounts: subs
    };
  }

  function buildResolvedFromCampaign(code, primaryCampaign, current, childCampaigns, coreCampaigns) {
    var primary = buildSingleCampaignResolved(code, primaryCampaign, current);
    // Cores first, then primary, then children. Order doesn't affect the
    // combinability matrix (which is set-based) but makes per-variant traces
    // easier to read: "core was here, then the cookie code, then chained".
    var discounts = [];
    if (Array.isArray(coreCampaigns)) {
      for (var coi = 0; coi < coreCampaigns.length; coi++) {
        discounts.push(buildSingleCampaignResolved(coreCampaigns[coi].code, coreCampaigns[coi], current));
      }
    }
    discounts.push(primary);
    if (Array.isArray(childCampaigns)) {
      for (var ci = 0; ci < childCampaigns.length; ci++) {
        discounts.push(buildSingleCampaignResolved(childCampaigns[ci].code, childCampaigns[ci], current));
      }
    }
    return {
      active: true,
      code: primary.code,
      type: primary.type,
      value: primary.value,
      value_type: primary.value_type,
      eligible: primary.eligible,
      target_selection: primary.target_selection,
      target_type: primary.target_type,
      raw_app: null,
      cart_token: null,
      source: 'static-config:campaign',
      campaign_code: primary.campaign_code,
      discounts: discounts
    };
  }

  // POST /discount/<code>?redirect=/cart.js for every code passed in. Returns
  // a Promise.all so the caller can deterministically chain post-apply work
  // (re-fetching /cart.js, dispatching the final resolved state, etc).
  //
  // The CALLER decides which codes to fire (typically by filtering out codes
  // that are already on the cart via indexCartDiscountCodes). This function
  // does no internal de-duplication — it fires what it's told.
  function stackDiscountsOnCart(codes) {
    if (!Array.isArray(codes) || codes.length === 0) return Promise.resolve();
    var pending = [];
    for (var i = 0; i < codes.length; i++) {
      var code = String(codes[i] || '').trim();
      if (!code) continue;
      pending.push((function (theCode) {
        try {
          return fetch('/discount/' + encodeURIComponent(theCode) + '?redirect=/cart.js', {
            credentials: 'same-origin',
            cache: 'no-store'
          })
            .then(function () { DBG.log('discount applied on cart', theCode); })
            .catch(function (err) { DBG.warn('discount apply failed', theCode, err); });
        } catch (e) {
          DBG.warn('discount fetch threw', theCode, e);
          return Promise.resolve();
        }
      })(code));
    }
    return pending.length > 0 ? Promise.all(pending) : Promise.resolve();
  }

  // Attach core-discount codes to the cart session (firing /discount/<code>
  // for each one not already present in the cart). After cores are attached,
  // re-fires `restoreCookieCode` last so the discount cookie ends as the
  // primary code the engine expects on the next page load — otherwise the
  // cookie would be whichever core code was POSTed last, and the engine
  // would route the next resolve() through the wrong flow (campaign vs
  // influencer vs single).
  //
  // Why this exists: cores (`core:true` in the JSON) are paint-as-always-on
  // by intent, but if the Shopify-side discount is CODE-based (not
  // automatic), Shopify only applies it when the code is on the cart
  // session. The original "core" assumption was that they'd be automatic
  // discounts that always apply; this helper closes the gap for code-based
  // cores.
  function attachCoresAndRestoreCookie(coreCodes, restoreCookieCode, cart) {
    var idx = indexCartDiscountCodes(cart);
    var missing = (coreCodes || []).filter(function (c) {
      return c && !idx.present[String(c).toLowerCase().trim()];
    });
    if (missing.length === 0) {
      DBG.t('CORES-ATTACH', 'all cores already on cart, no /discount/ POSTs needed', {
        cores: coreCodes,
        cart_present: Object.keys(idx.present)
      });
      return Promise.resolve();
    }
    DBG.t('CORES-ATTACH', 'firing /discount/ for missing core codes', {
      missing: missing,
      already_present: Object.keys(idx.present),
      restore_cookie_to: restoreCookieCode || null
    });
    return stackDiscountsOnCart(missing).then(function () {
      if (restoreCookieCode) {
        DBG.t('CORES-ATTACH', 'restoring cookie to primary by re-firing it last', { code: restoreCookieCode });
        return stackDiscountsOnCart([restoreCookieCode]);
      }
    });
  }

  // Build a lowercase-keyed index of cart.discount_codes for O(1) lookup of
  // "is this code on the cart at all" (present) and "is Shopify currently
  // honoring it" (applicable). Empty/missing cart returns an empty index.
  function indexCartDiscountCodes(cart) {
    var idx = { present: {}, applicable: {} };
    if (!cart || !Array.isArray(cart.discount_codes)) return idx;
    for (var i = 0; i < cart.discount_codes.length; i++) {
      var dc = cart.discount_codes[i] || {};
      if (!dc.code) continue;
      var key = String(dc.code).toLowerCase().trim();
      idx.present[key] = true;
      if (dc.applicable) idx.applicable[key] = true;
    }
    return idx;
  }

  function cartIsEmpty(cart) {
    if (!cart) return true;
    var n = (cart.items && cart.items.length) || cart.item_count || 0;
    return n === 0;
  }

  // Given a synthesized resolved (built from JSON config) and a /cart.js
  // snapshot, return the resolved state we should actually dispatch.
  //
  // Rules:
  //   - Empty cart -> trust synthesized. Shopify marks EVERY discount_code
  //     applicable:false when there are no items in the cart, so applicability
  //     is uninformative here. The PDP is the customer's first impression of
  //     the offer — paint the synthesized prices and let real cart values
  //     render via Liquid when items get added.
  //   - Non-empty cart -> for each synthesized sub-discount, check whether its
  //     code is in cart.discount_codes with applicable:true. Drop the
  //     non-applicable ones. This catches typos, expired codes, customer
  //     ineligibility, and combinability mismatches without giving a false
  //     positive on empty-cart sessions.
  //
  // Returns one of:
  //   - the synthesized resolved unchanged (no codes dropped)
  //   - a corrected resolved with only the surviving sub-discounts
  //   - { active: false, source: 'cart-truth:all-rejected', ... }
  function filterByCartTruth(synthesized, cart) {
    if (!synthesized || !synthesized.active) return synthesized;
    if (cartIsEmpty(cart)) {
      DBG.t('FILTER', 'cart empty -> trusting synthesized (applicable flag is meaningless on empty cart)');
      return synthesized;
    }

    var cartIndex = indexCartDiscountCodes(cart);
    var subs = Array.isArray(synthesized.discounts) && synthesized.discounts.length > 0
      ? synthesized.discounts
      : [synthesized];

    var kept = [];
    var dropped = [];
    var decisions = [];
    for (var si = 0; si < subs.length; si++) {
      var sub = subs[si];
      var key = String(sub.code || synthesized.code || '').toLowerCase().trim();
      // Cores are trusted from JSON config. They typically map to is_automatic
      // discounts in Shopify which DON'T appear in cart.discount_codes, so
      // checking applicability here would always reject them. Skip the cart
      // check for cores and trust the static config.
      if (sub.core === true) {
        kept.push(sub);
        decisions.push({ code: sub.code, decision: 'kept-core', cart_present: !!cartIndex.present[key], note: 'core: skip cart check' });
        continue;
      }
      if (cartIndex.applicable[key]) {
        kept.push(sub);
        decisions.push({ code: sub.code, decision: 'kept', cart_present: true, cart_applicable: true });
      } else {
        dropped.push(sub.code);
        decisions.push({
          code: sub.code,
          decision: 'dropped',
          cart_present: !!cartIndex.present[key],
          cart_applicable: false
        });
      }
    }
    DBG.t('FILTER', 'per-code decisions', { decisions: decisions, kept: kept.length, dropped: dropped.length });

    if (dropped.length === 0) {
      return synthesized;
    }

    DBG.tw('FILTER', 'Shopify rejected codes', { rejected: dropped });

    if (kept.length === 0) {
      return {
        active: false,
        code: synthesized.code,
        source: 'cart-truth:all-rejected',
        rejected_codes: dropped
      };
    }

    // When the synthesized had a "primary" cookie code that got rejected but
    // cores survived, the top-level code should reflect the surviving primary
    // (first non-core kept entry), falling back to the first kept overall.
    var primary = null;
    for (var pk = 0; pk < kept.length; pk++) {
      if (!kept[pk].core) { primary = kept[pk]; break; }
    }
    if (!primary) primary = kept[0];
    var corrected = {
      active: true,
      code: primary.code,
      type: primary.type,
      value: primary.value,
      value_type: primary.value_type,
      eligible: primary.eligible,
      target_selection: primary.target_selection,
      target_type: primary.target_type,
      source: 'cart-truth:filtered',
      rejected_codes: dropped
    };
    if (kept.length > 1) corrected.discounts = kept;
    return corrected;
  }

  // Shallow equality on the fields that drive paint behavior. Used to suppress
  // redundant re-dispatches that would otherwise cause visible flicker when
  // the deterministic pipeline arrives at the same state across phase 1 and
  // phase 2 reads.
  function resolvedShallowEquals(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (!!a.active !== !!b.active) return false;
    if (!a.active) return true;
    if (String(a.code || '') !== String(b.code || '')) return false;
    if (a.type !== b.type) return false;
    if (Number(a.value || 0) !== Number(b.value || 0)) return false;
    var aLen = Array.isArray(a.discounts) ? a.discounts.length : 1;
    var bLen = Array.isArray(b.discounts) ? b.discounts.length : 1;
    if (aLen !== bLen) return false;
    return true;
  }

  function nowMs() {
    return new Date().getTime();
  }

  function readCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function readUrlParam() {
    try {
      var params = new URLSearchParams(window.location.search || '');
      for (var i = 0; i < URL_PARAMS.length; i++) {
        var v = params.get(URL_PARAMS[i]);
        if (v) return v;
      }
    } catch (e) { /* no-op */ }
    return null;
  }

  function readCachedResult(code) {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (!parsed.timestamp || (nowMs() - parsed.timestamp) > STORAGE_TTL_MS) return null;
      if ((parsed.code || '').toLowerCase() !== (code || '').toLowerCase()) return null;
      return parsed.resolved || null;
    } catch (e) {
      return null;
    }
  }

  function writeCachedResult(code, resolved) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        code: code,
        timestamp: nowMs(),
        resolved: resolved
      }));
    } catch (e) { /* quota or disabled - fine */ }
  }

  function clearCache() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) { /* no-op */ }
  }

  function fetchJSON(url, opts) {
    return fetch(url, Object.assign({
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }, opts || {})).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  function applyDiscountToCart(code) {
    return fetchJSON('/cart/update.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ discount: code })
    });
  }

  function getCart() {
    return fetchJSON('/cart.js');
  }

  function appMatchesCode(app, code) {
    var lc = (code || '').toLowerCase();
    return (app.title || '').toLowerCase() === lc
      || (app.code || '').toLowerCase() === lc;
  }

  function buildEligibilityFromCart(cart, code) {
    var eligible = {};
    var cartLevelApps = (cart && cart.cart_level_discount_applications) || [];
    var legacyApps = (cart && cart.discount_applications) || [];
    var apps = cartLevelApps.concat(legacyApps);

    var matchingApp = null;
    for (var i = 0; i < apps.length; i++) {
      if (appMatchesCode(apps[i] || {}, code)) {
        matchingApp = apps[i];
        break;
      }
    }
    if (!matchingApp && apps.length === 1) {
      matchingApp = apps[0];
    }

    if (!matchingApp) {
      var scanItems = (cart && cart.items) || [];
      for (var si = 0; si < scanItems.length; si++) {
        var scanAllocs = (scanItems[si] && (scanItems[si].line_level_discount_allocations || scanItems[si].discount_allocations)) || [];
        for (var sa = 0; sa < scanAllocs.length; sa++) {
          var lineApp = (scanAllocs[sa] && scanAllocs[sa].discount_application) || null;
          if (lineApp && appMatchesCode(lineApp, code)) {
            matchingApp = lineApp;
            apps = apps.concat([lineApp]);
            break;
          }
        }
        if (matchingApp) break;
      }
    }

    var items = (cart && cart.items) || [];
    for (var j = 0; j < items.length; j++) {
      var item = items[j] || {};
      var saved = 0;

      // Modern shape: line_level_discount_allocations[*].discount_application + amount
      var lineAllocs = item.line_level_discount_allocations || item.discount_allocations || [];
      var savedFromLineAllocs = 0;
      for (var k = 0; k < lineAllocs.length; k++) {
        var alloc = lineAllocs[k] || {};
        var allocApp = alloc.discount_application || {};
        var matches = appMatchesCode(allocApp, code) || (apps.length === 1);
        if (matches) savedFromLineAllocs += Number(alloc.amount || 0);
      }
      saved += savedFromLineAllocs;

      // item.discounts[] mirrors line_level_discount_allocations[] for entitled-target
      // codes; only consult it as a fallback when line allocations contributed nothing
      // for this item, to avoid double-counting the same per-code amount.
      if (savedFromLineAllocs === 0) {
        var lineDiscounts = item.discounts || [];
        for (var d = 0; d < lineDiscounts.length; d++) {
          var disc = lineDiscounts[d] || {};
          if ((disc.title || '').toLowerCase() === (code || '').toLowerCase()) {
            saved += Number(disc.amount || 0);
          }
        }
      }

      // Total-discount fallback: when only one cart-level discount is in play
      // and matches our code, item.total_discount captures the across-allocated
      // amount even when neither line_level_discount_allocations nor discounts
      // resolves into a per-code entry.
      if (saved === 0 && matchingApp && appMatchesCode(matchingApp, code)) {
        var totalDisc = Number(item.total_discount || 0);
        if (totalDisc > 0) saved = totalDisc;
      }

      if (saved > 0) {
        var pid = String(item.product_id);
        var vid = String(item.variant_id);
        if (!eligible[pid]) eligible[pid] = { savings_total: 0, variants: {} };
        eligible[pid].savings_total += saved;
        eligible[pid].variants[vid] = (eligible[pid].variants[vid] || 0) + (saved / Math.max(1, item.quantity));
      }
    }
    return { eligible: eligible, app: matchingApp, allApps: apps };
  }

  function appType(app) {
    if (!app) return 'unknown';
    var v = app.value_type || app.type || '';
    var typeStr = (app.type || '').toLowerCase();
    if (typeStr === 'shipping_discount' || typeStr === 'shipping') return 'shipping';
    if (typeStr === 'automatic') {
      // Automatic discounts may also be percentage/fixed; rely on value_type
    }
    if (v === 'percentage') return 'percentage';
    if (v === 'fixed_amount') return 'fixed';
    if (typeStr === 'discount_code' && (app.target_type === 'line_item' || !app.target_type)) {
      return v === 'percentage' ? 'percentage' : 'fixed';
    }
    return typeStr || 'unknown';
  }

  function buildResolved(code, cart) {
    if (!cart) {
      return { active: false, code: code || null };
    }
    var built = buildEligibilityFromCart(cart, code);
    var app = built.app;
    if (!app) {
      return { active: false, code: code || null };
    }
    DBG.log('matched app', app, 'all apps:', built.allApps);
    var type = appType(app);
    if (type === 'shipping') {
      return {
        active: true,
        code: code || app.title || app.code || null,
        type: 'shipping',
        value: Number(app.value || 0),
        value_type: app.value_type || null,
        eligible: {},
        target_selection: app.target_selection || null,
        raw_app: app,
        cart_token: cart.token || null
      };
    }
    var resolvedValue = Number(app.value || 0);

    // Shopify Function-based automatic discounts (DiscountAutomaticApp) often
    // expose value="" / value_type=null in the storefront cart payload, so we
    // can't read the percentage off the app object directly. When that happens
    // but the cart has at least one line allocation contributed by this code,
    // derive the percentage from the actual savings amount: pct = (savings /
    // quantity) / unit_price * 100. This lets other variants of the same
    // product (which getDiscountForVariant in price-override.js falls through
    // to the %-formula for) display the correct discounted price too.
    if (type === 'percentage' && (!isFinite(resolvedValue) || resolvedValue <= 0)) {
      var items = (cart && cart.items) || [];
      for (var ix = 0; ix < items.length; ix++) {
        var it = items[ix] || {};
        var unitPrice = Number(it.price || 0);
        if (unitPrice <= 0) continue;
        var qty = Math.max(1, Number(it.quantity || 1));
        var allocs = it.line_level_discount_allocations || it.discount_allocations || [];
        var totalLineSaved = 0;
        for (var ja = 0; ja < allocs.length; ja++) {
          var aApp = (allocs[ja] && allocs[ja].discount_application) || {};
          if (appMatchesCode(aApp, code) || built.allApps.length === 1) {
            totalLineSaved += Number(allocs[ja].amount || 0);
          }
        }
        if (totalLineSaved > 0) {
          var pct = (totalLineSaved / qty) / unitPrice * 100;
          // Snap to typical merchant-set values (5/10/15/20/25/30/...): if the
          // derived value is within 0.5 of a round number, round to it.
          var rounded = Math.round(pct);
          if (Math.abs(pct - rounded) < 0.5) pct = rounded;
          resolvedValue = pct;
          DBG.log('derived percentage from line allocation', { pct: pct, totalLineSaved: totalLineSaved, qty: qty, unitPrice: unitPrice });
          break;
        }
      }
    }

    return {
      active: true,
      code: code || app.title || app.code || null,
      type: type,
      value: resolvedValue,
      value_type: app.value_type || null,
      total_allocated: Number(app.total_allocated_amount || 0),
      eligible: built.eligible,
      target_selection: app.target_selection || null,
      target_type: app.target_type || null,
      raw_app: app,
      cart_token: cart.token || null
    };
  }

  var LAST_DISPATCHED = null;
  var DISPATCH_SEQ = 0;

  // Distill a resolved object to a compact, log-friendly summary. The full
  // resolved can be huge (eligibility maps, raw_app, cart_token, etc); the
  // summary surfaces only the fields that drive paint behavior + a diff hint
  // (codes[] flattened to a comma-list, per-discount value).
  function summarizeResolved(r) {
    if (!r) return { null: true };
    if (!r.active) {
      return { active: false, code: r.code || null, source: r.source || null, rejected: r.rejected_codes || null };
    }
    var subs = Array.isArray(r.discounts) && r.discounts.length > 0 ? r.discounts : [r];
    var codes = subs.map(function (s) {
      var bucket = s.eligible && Object.keys(s.eligible)[0];
      var variants = bucket && s.eligible[bucket] && s.eligible[bucket].variants
        ? Object.keys(s.eligible[bucket].variants).length
        : 0;
      return s.code + '(' + s.type + ':' + s.value + ',variants=' + variants + ')';
    });
    return {
      active: true,
      code: r.code,
      type: r.type,
      value: r.value,
      source: r.source || null,
      discount_count: subs.length,
      discounts: codes
    };
  }

  function dispatchResolved(resolved) {
    DISPATCH_SEQ++;
    var changed = LAST_DISPATCHED
      ? {
          code_changed: String((LAST_DISPATCHED || {}).code || '') !== String((resolved || {}).code || ''),
          active_changed: !!(LAST_DISPATCHED || {}).active !== !!(resolved || {}).active,
          type_changed: (LAST_DISPATCHED || {}).type !== (resolved || {}).type,
          value_changed: Number((LAST_DISPATCHED || {}).value || 0) !== Number((resolved || {}).value || 0)
        }
      : { first_dispatch: true };
    DBG.t('DISPATCH', 'dispatch #' + DISPATCH_SEQ, { resolved: summarizeResolved(resolved), changed: changed });
    LAST_DISPATCHED = resolved;
    window.DentisActiveDiscount = resolved;
    try {
      var ev = new CustomEvent(EVENT_NAME, { detail: resolved });
      document.dispatchEvent(ev);
    } catch (e) {
      DBG.tw('DISPATCH', 'CustomEvent constructor failed, using legacy fallback', { error: String(e) });
      var fallback = document.createEvent('CustomEvent');
      fallback.initCustomEvent(EVENT_NAME, false, false, resolved);
      document.dispatchEvent(fallback);
    }
  }

  // Dispatch suppressed when shallowly equal to the previous dispatch — keeps
  // the deterministic pipeline (read cart -> apply missing -> read cart ->
  // dispatch) from firing a redundant event that would force price-override
  // to restoreAll() + re-paint for the same final state. Eliminates the
  // visible-flicker class of bugs where two dispatches arrive at identical
  // resolved states across an async boundary.
  function dispatchResolvedOnce(resolved) {
    if (resolvedShallowEquals(LAST_DISPATCHED, resolved)) {
      DBG.t('DISPATCH-X', 'suppressed (identical to last)', { resolved: summarizeResolved(resolved) });
      return;
    }
    dispatchResolved(resolved);
  }

  // Serializes concurrent resolve() calls. Both the DOMContentLoaded handler
  // and the autoapply snippet's nudgeEngine -> refresh path can fire resolve()
  // around the same time; running both flows in parallel would double the
  // /discount/ POSTs and risk two competing dispatches.
  var RESOLVE_IN_FLIGHT = null;

  function resolve(explicitCode) {
    // Trace the caller so the user can see WHO triggered resolve in the
    // console (DOMContentLoaded handler, autoapply nudge -> refresh, manual
    // window.DentisDiscountEngine.refresh() call, etc).
    var callerHint = 'unknown';
    try {
      var stack = (new Error()).stack || '';
      if (stack.indexOf('refresh') !== -1) callerHint = 'refresh';
      else if (stack.indexOf('DOMContentLoaded') !== -1) callerHint = 'DOMContentLoaded';
      else if (stack.indexOf('resolve') !== -1) callerHint = 'direct/init';
    } catch (e) { /* ignore */ }
    DBG.t('RESOLVE', 'entry', { explicitCode: explicitCode || null, in_flight: !!RESOLVE_IN_FLIGHT, caller: callerHint });

    if (RESOLVE_IN_FLIGHT) {
      DBG.tw('RESOLVE-X', 'already in-flight; coalescing', { caller: callerHint });
      return RESOLVE_IN_FLIGHT;
    }

    var urlCode = readUrlParam();
    var cookieCode = readCookie(COOKIE_NAME);
    var trimmedExplicit = explicitCode ? String(explicitCode).trim() : '';
    // Cookie wins over explicit and url. The cookie reflects what Shopify
    // actually has on the cart session — when the customer arrived via
    // /discount/CAMPAIGN, the cookie is the campaign code. Callers passing
    // a different code (typically the autoapply snippet nudging with the
    // theme-settings storewide code) must not override that.
    var code = cookieCode || trimmedExplicit || urlCode;
    DBG.t('RESOLVE', 'code-pick', { explicitCode: trimmedExplicit, urlCode: urlCode, cookieCode: cookieCode, picked: code });

    var staticCfg = readStaticPromoConfig();
    DBG.t('RESOLVE', 'static-config-loaded', {
      has_single: !!(staticCfg && staticCfg.single),
      single_code: staticCfg && staticCfg.single && staticCfg.single.code || null,
      has_campaigns: !!(staticCfg && staticCfg.campaigns),
      campaign_count: staticCfg && staticCfg.campaigns ? staticCfg.campaigns.length : 0,
      current: staticCfg && staticCfg.current || null
    });

    // Cores resolve from JSON regardless of any cookie. They paint as default
    // bundle-deal prices for the current PDP scope (e.g. DUO-PAKKEPRIS on the
    // Duo variant). Whether a core survives alongside the cookie's discount
    // is decided per-variant in price-override via the combinability matrix.
    var coreCampaigns = collectCoreCampaigns(staticCfg);
    if (coreCampaigns.length > 0) {
      DBG.t('CORES', 'matched ' + coreCampaigns.length + ' core campaign(s) for current PDP', {
        cores: coreCampaigns.map(function (c) { return c.code + (c.percentage ? '(' + c.percentage + '%)' : '(' + c.amount + 'kr)'); })
      });
    }

    if (!code) {
      // No cookie, no explicit, no URL — but if cores match this PDP, paint
      // them as the resolved state. Empty inactive dispatch only when there's
      // truly nothing to apply.
      if (coreCampaigns.length > 0) {
        var coresOnlyResolved = buildCoresOnlyResolved(coreCampaigns, staticCfg.current);
        var coreCodesNoCookie = coreCampaigns.map(function (c) { return c.code; });
        DBG.t('RESOLVE', 'no cookie -> attaching cores then dispatching cores-only', { cores: coreCodesNoCookie });
        RESOLVE_IN_FLIGHT = getCart()
          .catch(function (err) { DBG.tw('CORES-P1', '/cart.js failed', { error: String(err) }); return null; })
          .then(function (cart1) {
            return attachCoresAndRestoreCookie(coreCodesNoCookie, null, cart1)
              .then(function () {
                var out = filterByCartTruth(coresOnlyResolved, cart1);
                dispatchResolvedOnce(out);
              });
          })
          .catch(function () { dispatchResolvedOnce(coresOnlyResolved); })
          .then(function () { RESOLVE_IN_FLIGHT = null; }, function () { RESOLVE_IN_FLIGHT = null; });
        return RESOLVE_IN_FLIGHT;
      }
      DBG.t('RESOLVE', 'no code anywhere, dispatching inactive');
      dispatchResolved({ active: false, code: null });
      return;
    }
    code = String(code).trim();
    if (!code) {
      if (coreCampaigns.length > 0) {
        var coresOnlyResolved2 = buildCoresOnlyResolved(coreCampaigns, staticCfg.current);
        var coreCodesEmptyCookie = coreCampaigns.map(function (c) { return c.code; });
        DBG.t('RESOLVE', 'empty cookie -> attaching cores then dispatching cores-only', { cores: coreCodesEmptyCookie });
        RESOLVE_IN_FLIGHT = getCart()
          .catch(function (err) { DBG.tw('CORES-P1', '/cart.js failed', { error: String(err) }); return null; })
          .then(function (cart1) {
            return attachCoresAndRestoreCookie(coreCodesEmptyCookie, null, cart1)
              .then(function () {
                var out = filterByCartTruth(coresOnlyResolved2, cart1);
                dispatchResolvedOnce(out);
              });
          })
          .catch(function () { dispatchResolvedOnce(coresOnlyResolved2); })
          .then(function () { RESOLVE_IN_FLIGHT = null; }, function () { RESOLVE_IN_FLIGHT = null; });
        return RESOLVE_IN_FLIGHT;
      }
      DBG.t('RESOLVE', 'code resolved to empty string, dispatching inactive');
      dispatchResolved({ active: false, code: null });
      return;
    }

    // Multi-campaign match: cookie code matches a configured campaign AND the
    // current PDP is in that campaign's scope (templates / product_handles /
    // variant_ids). Deterministic pipeline:
    //
    //   Phase 1: getCart() -> snapshot existing cart state (what codes are
    //            ALREADY on the session from prior pages / prior visits).
    //   Phase 2: From the snapshot, determine which expected codes (primary +
    //            children) are missing. Empty list = fast path, dispatch once
    //            from phase 1's cart and we're done. Otherwise...
    //   Phase 3: Fire /discount/<code>?redirect=/cart.js for each missing
    //            code in parallel, await all settlements.
    //   Phase 4: Re-read /cart.js to get the post-apply ground truth.
    //   Phase 5: Build the final resolved via filterByCartTruth and dispatch
    //            ONCE (dispatchResolvedOnce dedupes against the phase 1
    //            dispatch when the result is identical).
    //
    // No optimistic "paint now, correct later" — flicker eliminated.
    if (staticCfg && staticCfg.campaigns) {
      var matchedCampaign = matchCampaign(staticCfg, code);
      if (matchedCampaign) {
        var childCampaigns = collectChildCampaigns(staticCfg, matchedCampaign);
        // Pass cores into the synthesizer — they end up in resolved.discounts
        // first, then primary, then chained children. Filter out the primary
        // and chain from cores to avoid duplicates if the cookie code itself
        // is a core (rare but possible).
        var coresForCampaign = coreCampaigns.filter(function (c) {
          if (c.code === matchedCampaign.code) return false;
          for (var cci = 0; cci < childCampaigns.length; cci++) {
            if (c.code === childCampaigns[cci].code) return false;
          }
          return true;
        });
        var synthesized = buildResolvedFromCampaign(code, matchedCampaign, staticCfg.current, childCampaigns, coresForCampaign);
        var childCodesForCart = collectChildCodesForCart(staticCfg, matchedCampaign) || [];
        // Cores must also be attached as discount codes so Shopify checkout
        // actually applies them — `core:true` is a paint-as-always-on flag,
        // but if the underlying Shopify discount is code-based (not
        // automatic) it only applies when its code is on the cart session.
        var coreCodesForCart = coresForCampaign.map(function (c) { return c.code; });
        // Primary LAST so /discount/<primary> wins the cookie restore.
        var allExpectedCodes = coreCodesForCart.concat(childCodesForCart).concat([code]);

        DBG.t('CAMPAIGN', 'matched primary + chain + cores', {
          primary: matchedCampaign.code,
          primary_type: matchedCampaign.amount ? 'fixed:' + matchedCampaign.amount : 'percentage:' + matchedCampaign.percentage,
          primary_variants: matchedCampaign.variant_ids || [],
          primary_combines: Combinability.normalize(matchedCampaign),
          child_count_paint: childCampaigns.length,
          child_codes_paint: childCampaigns.map(function (c) { return c.code; }),
          child_codes_cart: childCodesForCart,
          core_count: coresForCampaign.length,
          core_codes: coresForCampaign.map(function (c) { return c.code; }),
          synthesized: summarizeResolved(synthesized),
          all_expected_codes: allExpectedCodes
        });

        RESOLVE_IN_FLIGHT = getCart()
          .catch(function (err) { DBG.tw('P1-CART', '/cart.js failed', { error: String(err) }); return null; })
          .then(function (cart1) {
            DBG.t('P1-CART', 'snapshot', {
              item_count: cart1 ? (cart1.item_count || (cart1.items && cart1.items.length) || 0) : null,
              discount_codes: cart1 && cart1.discount_codes || null,
              token: cart1 && cart1.token || null
            });

            var idx1 = indexCartDiscountCodes(cart1);
            var missingCodes = allExpectedCodes.filter(function (c) {
              return !idx1.present[String(c).toLowerCase().trim()];
            });
            DBG.t('P2-DIFF', 'missing codes', {
              all_expected: allExpectedCodes,
              already_present: Object.keys(idx1.present),
              already_applicable: Object.keys(idx1.applicable),
              missing: missingCodes
            });

            if (missingCodes.length === 0) {
              DBG.t('P5-FAST', 'all codes on cart already; fast-path dispatch');
              var fast = filterByCartTruth(synthesized, cart1);
              DBG.t('FILTER', 'fast-path result', { input: summarizeResolved(synthesized), output: summarizeResolved(fast) });
              dispatchResolvedOnce(fast);
              return;
            }

            DBG.t('P3-FIRE', 'firing /discount/ POSTs', { codes: missingCodes });
            return stackDiscountsOnCart(missingCodes)
              .then(function () {
                DBG.t('P4-CART', 'phase 4 re-read /cart.js');
                return getCart().catch(function (err) {
                  DBG.tw('P4-CART', '/cart.js failed, falling back to cart1', { error: String(err) });
                  return cart1;
                });
              })
              .then(function (cart2) {
                DBG.t('P4-CART', 'snapshot after POSTs', {
                  item_count: cart2 ? (cart2.item_count || (cart2.items && cart2.items.length) || 0) : null,
                  discount_codes: cart2 && cart2.discount_codes || null
                });
                var final = filterByCartTruth(synthesized, cart2 || cart1);
                DBG.t('FILTER', 'final result', { input: summarizeResolved(synthesized), output: summarizeResolved(final) });
                dispatchResolvedOnce(final);
              });
          })
          .catch(function (err) {
            DBG.te('CAMPAIGN', 'flow failed, dispatching synthesized as last-resort', { error: String(err) });
            dispatchResolvedOnce(synthesized);
          })
          .then(function () { RESOLVE_IN_FLIGHT = null; DBG.t('RESOLVE-END', 'campaign flow finalized'); }, function () { RESOLVE_IN_FLIGHT = null; });
        return RESOLVE_IN_FLIGHT;
      } else {
        DBG.t('CAMPAIGN', 'no campaign matched current scope', {
          code: code,
          current_template: staticCfg.current && staticCfg.current.template_suffix,
          current_handle: staticCfg.current && staticCfg.current.product_handle,
          current_variants: staticCfg.current && staticCfg.current.product_variant_ids
        });
      }
    }

    if (staticCfg && staticCfg.single && staticCfg.single.code.toLowerCase() === code.toLowerCase()) {
      // Single = order-kind storewide. Combine with cores into a single
      // discounts[] payload so price-override can decide per-variant via
      // the combinability matrix whether to stack or override.
      var singleBase = buildResolvedFromStaticConfig(code, staticCfg.single);
      var subs = [];
      for (var ssi = 0; ssi < coreCampaigns.length; ssi++) {
        subs.push(buildSingleCampaignResolved(coreCampaigns[ssi].code, coreCampaigns[ssi], staticCfg.current));
      }
      subs.push(singleBase);
      var singleSynthesized = subs.length > 1
        ? Object.assign({}, singleBase, { discounts: subs, source: 'single+cores' })
        : singleBase;
      DBG.t('SINGLE', 'matched storewide single (+cores)', {
        single: staticCfg.single,
        core_count: coreCampaigns.length,
        synthesized: summarizeResolved(singleSynthesized)
      });
      RESOLVE_IN_FLIGHT = getCart()
        .catch(function (err) { DBG.tw('SINGLE-P1', '/cart.js failed', { error: String(err) }); return null; })
        .then(function (cart) {
          DBG.t('SINGLE-P1', 'snapshot', {
            item_count: cart ? (cart.item_count || (cart.items && cart.items.length) || 0) : null,
            discount_codes: cart && cart.discount_codes || null
          });
          var out = filterByCartTruth(singleSynthesized, cart);
          DBG.t('FILTER', 'single result', { input: summarizeResolved(singleSynthesized), output: summarizeResolved(out) });
          dispatchResolvedOnce(out);
        })
        .catch(function (err) {
          DBG.te('SINGLE', 'flow failed, dispatching synthesized as last-resort', { error: String(err) });
          dispatchResolvedOnce(singleSynthesized);
        })
        .then(function () { RESOLVE_IN_FLIGHT = null; DBG.t('RESOLVE-END', 'single flow finalized'); }, function () { RESOLVE_IN_FLIGHT = null; });
      return RESOLVE_IN_FLIGHT;
    }

    // Influencer pattern: cookie code follows `<LETTERS>20` convention.
    // Treated as a 20%-off order-level discount that combines with cores +
    // anything else. Stacks naturally with cores via the per-variant matrix
    // composer (cross-kind product+order, both combines all-true).
    if (isInfluencerCode(code)) {
      var coresForInfluencer = coreCampaigns.filter(function (c) {
        return String(c.code).toLowerCase().trim() !== code.toLowerCase();
      });
      var synthesizedInfluencer = buildInfluencerResolved(code, coresForInfluencer, staticCfg.current);
      var coreCodesForInfluencer = coresForInfluencer.map(function (c) { return c.code; });
      DBG.t('INFLUENCER', 'matched pattern (' + INFLUENCER_DEFAULT_PERCENTAGE + '% order-level)', {
        code: code,
        core_count: coresForInfluencer.length,
        core_codes: coreCodesForInfluencer,
        synthesized: summarizeResolved(synthesizedInfluencer)
      });

      RESOLVE_IN_FLIGHT = getCart()
        .catch(function (err) { DBG.tw('INFLUENCER-P1', '/cart.js failed', { error: String(err) }); return null; })
        .then(function (cart1) {
          DBG.t('INFLUENCER-P1', 'snapshot', {
            item_count: cart1 ? (cart1.item_count || (cart1.items && cart1.items.length) || 0) : null,
            discount_codes: cart1 && cart1.discount_codes || null
          });
          return attachCoresAndRestoreCookie(coreCodesForInfluencer, code, cart1)
            .then(function () {
              // No-op if no cores were attached; otherwise re-read cart to
              // get the post-attach truth before filtering.
              if (!coreCodesForInfluencer.length) return cart1;
              return getCart().catch(function (err) {
                DBG.tw('INFLUENCER-P2', '/cart.js re-read failed, falling back to pre-attach snapshot', { error: String(err) });
                return cart1;
              });
            })
            .then(function (cart2) {
              DBG.t('INFLUENCER-P2', 'post-attach snapshot', {
                item_count: cart2 ? (cart2.item_count || (cart2.items && cart2.items.length) || 0) : null,
                discount_codes: cart2 && cart2.discount_codes || null
              });
              var out = filterByCartTruth(synthesizedInfluencer, cart2);
              DBG.t('FILTER', 'influencer result', { input: summarizeResolved(synthesizedInfluencer), output: summarizeResolved(out) });
              dispatchResolvedOnce(out);
            });
        })
        .catch(function (err) {
          DBG.te('INFLUENCER', 'flow failed, dispatching synthesized as last-resort', { error: String(err) });
          dispatchResolvedOnce(synthesizedInfluencer);
        })
        .then(function () { RESOLVE_IN_FLIGHT = null; DBG.t('RESOLVE-END', 'influencer flow finalized'); }, function () { RESOLVE_IN_FLIGHT = null; });
      return RESOLVE_IN_FLIGHT;
    }

    // No static-config match for this code. If cores exist for the current
    // PDP, attach them to the cart session (firing /discount/ for any
    // missing core code) and dispatch cores-only paint. Skip the live
    // probe — the unknown cookie code is either a customer-entered code we
    // don't pre-configure (Shopify will still apply it server-side, and
    // the engine reflects what cart-truth reports) or a stale cookie.
    // Cores remain valid either way.
    if (coreCampaigns.length > 0) {
      var fallbackCoreCodes = coreCampaigns.map(function (c) { return c.code; });
      DBG.t('CORES', 'unknown code "' + code + '" -> attaching cores + dispatching cores-only', {
        cores: fallbackCoreCodes,
        restore_cookie_to: code
      });
      var fallbackCoresResolved = buildCoresOnlyResolved(coreCampaigns, staticCfg.current);
      RESOLVE_IN_FLIGHT = getCart()
        .catch(function (err) { DBG.tw('CORES-P1', '/cart.js failed', { error: String(err) }); return null; })
        .then(function (cart1) {
          return attachCoresAndRestoreCookie(fallbackCoreCodes, code, cart1)
            .then(function () {
              if (!fallbackCoreCodes.length) return cart1;
              return getCart().catch(function () { return cart1; });
            })
            .then(function (cart2) {
              var out = filterByCartTruth(fallbackCoresResolved, cart2);
              dispatchResolvedOnce(out);
            });
        })
        .catch(function () { dispatchResolvedOnce(fallbackCoresResolved); })
        .then(function () { RESOLVE_IN_FLIGHT = null; }, function () { RESOLVE_IN_FLIGHT = null; });
      return RESOLVE_IN_FLIGHT;
    }

    var cached = readCachedResult(code);
    if (cached) {
      DBG.log('cache HIT for code', code, cached);
      dispatchResolvedOnce(cached);
      return;
    }
    DBG.log('cache MISS for code', code, '- POST /cart/update.js');

    RESOLVE_IN_FLIGHT = applyDiscountToCart(code)
      .then(function (resp) {
        DBG.log('POST /cart/update.js response', resp);
        return resp;
      })
      .catch(function (err) {
        DBG.warn('POST /cart/update.js failed (non-fatal, code may still be saved on cart)', err);
      })
      .then(function () {
        DBG.log('GET /cart.js');
        return getCart();
      })
      .then(function (cart) {
        DBG.log('GET /cart.js response', cart);
        var resolved = buildResolved(code, cart);
        DBG.log('buildResolved output', resolved);
        // No probing. Adding/removing a probe variant fires Shopify cart:add
        // analytics (GA4 add_to_cart, Meta Pixel AddToCart, Klaviyo, etc.) and
        // pollutes performance metrics. Eligibility for product-level
        // (target_selection='entitled') discounts is only known when the cart
        // already contains an entitled line. For empty-cart scenarios the
        // engine relies on the static promo config short-circuit (see top of
        // resolve()) — merchants configure the discount once in theme settings
        // and the snippet renders pre-cart without any /cart/* round trips.
        return resolved;
      })
      .then(function (resolved) {
        if (resolved && resolved.active) {
          writeCachedResult(code, resolved);
          DBG.log('cached resolved discount in localStorage');
        } else {
          clearCache();
          DBG.log('no active discount; cleared cache');
        }
        dispatchResolvedOnce(resolved || { active: false, code: code });
      })
      .catch(function (err) {
        DBG.error('resolve() failed', err);
        clearCache();
        dispatchResolvedOnce({ active: false, code: code });
      })
      .then(function () { RESOLVE_IN_FLIGHT = null; }, function () { RESOLVE_IN_FLIGHT = null; });
    return RESOLVE_IN_FLIGHT;
  }

  window.DentisDiscountEngine = {
    resolve: resolve,
    refresh: function (explicitCode) { clearCache(); resolve(explicitCode); },
    clearCache: clearCache,
    EVENT_NAME: EVENT_NAME
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resolve);
  } else {
    resolve();
  }
})();
